import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, of, takeUntil, tap } from 'rxjs';
import { DynamicQueryService } from '@services/analytics-services/core';
import {} from '@services/analytics-services/products';
import { ProductPerformanceStats } from '@services/analytics-services/products/models/product-performance-stats';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { ProcessingState, DateRangePresetType } from '@services/core-services/models';
import { DashboardStore } from '../dashboard/dashboard.store';
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { QueryType, ChartType } from '@services/analytics-services/core/models/matrix-stats';
import { OrderAnalyticsService } from '@services/analytics-services/orders';
import { OrderProductStats } from '@services/analytics-services/orders/models/order-product';
import { ProductCheckoutStats } from '@services/analytics-services/products/models/product-checkout-stats';

@Injectable()
export class DashboardProductsStore implements OnDestroy {
  // State
  private products = new BehaviorSubject<OrderProductStats[] | undefined>(undefined);
  private productCheckoutLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private productCheckoutAnalytics = new BehaviorSubject<ProductCheckoutStats[] | undefined>(
    undefined
  );

  private productPerformance = new BehaviorSubject<ProductPerformanceStats[] | undefined>(
    undefined
  );
  private topProductsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private productPerformanceLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private errorMessage = new BehaviorSubject<string>('');
  private productPerformanceErrorMessage = new BehaviorSubject<string>('');
  private destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly orderAnalyticsService: OrderAnalyticsService
  ) {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        tap(() => {
          this.errorMessage.next('');
          this.productPerformanceErrorMessage.next('');
          this.loadTopProducts().subscribe();
          this.loadProductPerformance().subscribe();
          this.loadProductCheckoutStat().subscribe();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getProductWiseCheckOutStats(): Observable<ProductCheckoutStats[] | undefined> {
    return this.productCheckoutAnalytics.asObservable();
  }

  getTopProducts(): Observable<OrderProductStats[] | undefined> {
    return this.products.asObservable();
  }

  getProductperformance(): Observable<ProductPerformanceStats[] | undefined> {
    return this.productPerformance.asObservable();
  }

  getTopProductsLoader(): Observable<ProcessingState> {
    return this.topProductsLoader.asObservable();
  }

  getProductPerformanceLoader(): Observable<ProcessingState> {
    return this.productPerformanceLoader.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  getProductPerformanceErrorMessage(): Observable<string> {
    return this.productPerformanceErrorMessage.asObservable();
  }

  loadTopProducts(): Observable<OrderProductStats[]> {
    this.topProductsLoader.next(ProcessingState.Loading);
    const context = this.dashboardStore.getRangeContextSnapshot();
    const { range, interval, preset } = context;
    return this.orderAnalyticsService
      .getTopSellingProducts({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
      })
      .pipe(
        tap((products: OrderProductStats[]) => {
          this.topProductsLoader.next(ProcessingState.Success);
          this.products.next(products);
        }),
        catchError(error => {
          this.topProductsLoader.next(ProcessingState.Error);
          this.handleError('Error loading top products', error);
          this.products.next(undefined); // Clear data on error
          return of([] as OrderProductStats[]); // Return empty array on error
        })
      );
  }

  loadProductPerformance(): Observable<ProductPerformanceStats[]> {
    this.productPerformanceLoader.next(ProcessingState.Loading);
    const context = this.dashboardStore.getRangeContextSnapshot();
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.CE_ITEM_STATS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap(products => {
          this.productPerformanceLoader.next(ProcessingState.Success);
          this.productPerformance.next(products);
        }),
        catchError(error => {
          this.productPerformanceLoader.next(ProcessingState.Error);
          this.handleError(
            'Error loading product performance',
            error,
            this.productPerformanceErrorMessage
          );
          this.productPerformance.next(undefined);
          return of([] as ProductPerformanceStats[]);
        })
      );
  }

  loadProductCheckoutStat(): Observable<ProductCheckoutStats[]> {
    this.productCheckoutLoader.next(ProcessingState.Loading);
    const context = this.dashboardStore.getRangeContextSnapshot();
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.CHECKOUT_FUNNEL,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((checkout: ProductCheckoutStats[]) => {
          this.productCheckoutLoader.next(ProcessingState.Success);
          this.productCheckoutAnalytics.next(checkout);
        }),
        catchError(error => {
          this.productCheckoutLoader.next(ProcessingState.Error);
          this.handleError('Error loading subsequent purchases', error);
          this.productCheckoutAnalytics.next(undefined);
          return of([] as ProductCheckoutStats[]);
        })
      );
  }

  private handleError(
    message: string,
    error: any,
    errorSubject: BehaviorSubject<string> = this.errorMessage
  ): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    errorSubject.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }
}
